import React, { useState } from 'react';
import { useTranslate } from '@hooks';
import { chain } from 'lodash';
import {
  FlexContainer,
  BadgeList,
  FlagList,
  Text,
  Button,
  DetailsBlock,
  BasicPopover,
} from '@dotone/react-ui-core';
import { ChevronsRight, Mail as MailIcon, User as UserIcon } from 'react-feather';
import AffiliateGenderIcon from './AffiliateGenderIcon';
import AffiliateFavoriteButton from './AffiliateFavoriteButton';
import AffiliateInternalNotesFormModal from './AffiliateInternalNotesFormModal';
import { formatAvatar, formatEarningRating, formatRating } from '../formatters';
import { formatCampaignApprovalStatus } from '../Campaigns/formatters';

const Empty = () => {
  const { tShared } = useTranslate();
  return tShared('common:na');
};

const CollapsibleNotes = ({ value }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Text
      muted
      className="border p-2 rounded-lg w-full"
      onClick={() => setExpanded((prev) => !prev)}
    >
      <div
        style={{
          flex: 1,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: expanded ? 'unset' : 5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'pre-wrap',
          cursor: 'pointer',
        }}
      >
        {value}
      </div>
    </Text>
  );
};

export const formatAffiliateType = (cell, { siteInfos }) => {
  const mediaCategories = chain(siteInfos)
    .filter((siteInfo) => !!siteInfo.mediaCategory)
    .map((siteInfo) => siteInfo.mediaCategory)
    .uniqBy('id')
    .value();

  if (mediaCategories.length) {
    return (
      <ul className="m-0 p-0 list-unstyled">
        {mediaCategories.map((mediaCategory) => (
          <li key={mediaCategory.id} className="text-nowrap">
            {mediaCategory.parentCategoryName} <ChevronsRight size={12} /> {mediaCategory.name}
          </li>
        ))}
      </ul>
    );
  }

  return <Empty />;
};

export const formatAffiliateStats = (row) => (
  <div>
    <span>{row.clicks > 0 ? row.clicks : '--'}</span>
    <span>/</span>
    <span>{row.captured > 0 ? row.captured : '--'}</span>
  </div>
);

export const formatCategoryGroups = (value) => (
  <BadgeList items={value} color="light" className="mr-1 text-sm" />
);

export const formatApprovalStatus = (
  cell,
  { campaign, ...affiliate },
  { t, onCampaignOpen, currentUser }
) => (
  <>
    {formatCampaignApprovalStatus(cell, { ...campaign, affiliate }, { t, currentUser })}
    {!currentUser.singleOffer && affiliate.hasMoreCampaigns && (
      <Button color="link" className="float-right p-0" onClick={() => onCampaignOpen(affiliate.id)}>
        {`${t('affiliate:actions.manage')}+`}
      </Button>
    )}
  </>
);

export const formatAffiliate = (affiliate, full, id) => (
  <FlexContainer column align="center" justify="center">
    <Text>{affiliate.id}</Text>
    {formatAvatar(affiliate, { className: 'my-1' })}
    {full && <Text className="mb-1">{affiliate.name}</Text>}
    <div className="d-flex align-items center justify-content-center">
      {affiliate.email && (
        <a href={`mailto:${affiliate.email}`} className="btn btn-link btn-sm px-0 mr-1">
          <MailIcon size={16} className="text-secondary" />
        </a>
      )}
      {affiliate.bio && (
        <div className="py-1 px-0 mr-1">
          <UserIcon
            size={16}
            className="text-secondary"
            id={`affiliate-bio-${id}-${affiliate.id}`}
          />
          <BasicPopover target={`affiliate-bio-${id}-${affiliate.id}`} content={affiliate.bio} />
        </div>
      )}
      <AffiliateFavoriteButton
        affiliateId={affiliate.id}
        value={affiliate.preference?.favorite}
        className="p-0 mr-1"
      />
      {affiliate.gender && (
        <span className="py-1 px-0 mr-1">
          <AffiliateGenderIcon
            id={`affiliate-gender-${id}-${affiliate.id}`}
            value={affiliate.gender}
            size={18}
          />
        </span>
      )}
      {affiliate.country && (
        <FlagList
          countries={[affiliate.country]}
          target={`affiliate-flag-${id}-${affiliate.id}`}
          style={{ height: 12 }}
          className="px-0 my-2"
        />
      )}
    </div>
  </FlexContainer>
);

export const formatNetworkPerformance = (affiliate, t, tHint, id) => (
  <FlexContainer column justify="center" align="start" className="h-100 text-nowrap">
    <DetailsBlock
      label={t('affiliate:model.loginRating')}
      labelProps={{
        spanStyle: { fontSize: '10px' },
        hintProps: {
          ...tHint('affiliate:hints.loginRating'),
          tKey: `login-rating-${id}-${affiliate.id}`,
        },
      }}
      content={formatRating(affiliate.loginRating, affiliate)}
      className="mb-2"
    />
    <DetailsBlock
      label={t('affiliate:model.clickRating')}
      labelProps={{
        spanStyle: { fontSize: '10px' },
        hintProps: {
          ...tHint('affiliate:hints.clickRating'),
          tKey: `click-rating-${id}-${affiliate.id}`,
        },
      }}
      content={formatRating(affiliate.clickRating, affiliate)}
      className="mb-2"
    />
    <DetailsBlock
      label={t('affiliate:model.conversionRating')}
      labelProps={{
        spanStyle: { fontSize: '10px' },
        hintProps: {
          ...tHint('affiliate:hints.conversionRating'),
          tKey: `conversion-rating-${id}-${affiliate.id}`,
        },
      }}
      content={formatRating(affiliate.conversionRating, affiliate)}
      className="mb-0"
    />
  </FlexContainer>
);

export const formatNetworkHistory = (affiliate, t) => (
  <FlexContainer column justify="center" align="start" className="h-100 text-nowrap">
    <DetailsBlock
      label={t('affiliate:model.campaignMetrics')}
      labelProps={{ spanStyle: { fontSize: '10px' } }}
      content={formatEarningRating(affiliate.campaignMetrics, affiliate, t)}
      className="mb-2"
    />
    <DetailsBlock
      label={t('affiliate:model.earningRating')}
      labelProps={{ spanStyle: { fontSize: '10px' } }}
      content={formatEarningRating(affiliate.earningRating, affiliate, t)}
    />
  </FlexContainer>
);

export const formatInternalNotes = (affiliate, t) => (
  <div className="d-flex flex-column">
    <CollapsibleNotes value={affiliate.preference?.internalNotes || t('common:na')} />
    <AffiliateInternalNotesFormModal
      affiliate={affiliate}
      buttonProps={{
        children: t('affiliate:actions.upsertInternalNotes'),
        color: 'link',
        className: 'float-right',
      }}
    />
  </div>
);
